import { useMemo, useState } from 'react'

import { useBlocker } from '@/shared/hooks/useBlocker'
import {
  effectiveFilter,
  groupByStatus,
  visibleFilterOptions
} from '@/shared/lib/list-view'
import { isRecent } from '@/shared/lib/recency'
import { useJourneysStore } from '@/shared/store/useJourneysStore'
import type { BlockCondition, BlockResult } from '@/types/blocker'
import type { Journey } from '@/types/journey'
import type { Progress } from '@/types/progress'

import {
  JOURNEY_SORT_PRIORITY,
  JOURNEY_STATUS_LABELS,
  type JourneyFilterId,
  type JourneyUiStatus
} from '../constants/status'
import { JOURNEY_FILTER_OPTIONS } from '../lib/journeyFilterOptions'
import { matchesJourneyFilter } from '../lib/journeyFilterMatch'
import { getJourneyUiStatus } from '../lib/journeyUiStatus'

export type JourneyListItem = Journey & {
  _progress: Progress
  _totalCoinReward: number
  _totalPointReward: number
  _blockCondition: BlockCondition
  uiStatus: JourneyUiStatus
  /** Eixo independente de `uiStatus` — ver `shared/lib/recency`. */
  isNew: boolean
  blockResult: BlockResult
}

export const useJourneysListView = () => {
  const { journeys: rawJourneys, isDataLoaded } = useJourneysStore()
  const { checkBlockCondition } = useBlocker()
  const [rawFilter, setFilter] = useState<JourneyFilterId>('all')

  const items = useMemo((): JourneyListItem[] => {
    /* Um único instante para a lista inteira: dois itens criados no mesmo
       segundo têm de ser avaliados contra o mesmo `now`. */
    const now = Date.now()

    return rawJourneys.map(journey => {
      const blockResult = checkBlockCondition(journey._blockCondition)

      return {
        ...journey,
        blockResult,
        uiStatus: getJourneyUiStatus(journey, blockResult),
        isNew: isRecent(journey.created_at, now)
      }
    })
  }, [rawJourneys, checkBlockCondition])

  const visibleFilters = useMemo(
    () =>
      visibleFilterOptions(JOURNEY_FILTER_OPTIONS, id =>
        items.some(item => matchesJourneyFilter(item, id))
      ),
    [items]
  )

  /* Derivação, não efeito: o usuário filtra por "Novas", passa a janela de 1h,
     o chip desaparece — e um `useEffect` + `setFilter` renderizaria a lista
     vazia antes de corrigir. */
  const filter = effectiveFilter(rawFilter, visibleFilters)

  const sortedItems = useMemo(() => {
    const filtered =
      filter === 'all' ? items : items.filter(item => matchesJourneyFilter(item, filter))

    return [...filtered].sort((a, b) => {
      const priority =
        JOURNEY_SORT_PRIORITY[a.uiStatus] -
        JOURNEY_SORT_PRIORITY[b.uiStatus]

      if (priority !== 0) return priority

      return (a.position ?? 0) - (b.position ?? 0)
    })
  }, [items, filter])

  const globalProgress = useMemo(() => {
    const total = items.length
    const completed = items.filter(
      j => j.uiStatus === 'success'
    ).length
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0

    const nextJourney = items.find(
      j => j.uiStatus === 'available' || j.uiStatus === 'progress'
    )
    const nextRewardCoins = nextJourney?.coin_reward ?? nextJourney?._totalCoinReward

    return {
      completed,
      total,
      percent,
      nextRewardCoins: nextRewardCoins && nextRewardCoins > 0 ? nextRewardCoins : null
    }
  }, [items])

  /* Seções só na visão "Todas": com filtro específico todos os itens têm o
     mesmo status, e uma seção única seria ruído. */
  const groups = useMemo(
    () =>
      filter === 'all'
        ? groupByStatus(items, {
            order: JOURNEY_SORT_PRIORITY,
            labels: JOURNEY_STATUS_LABELS,
            statusOf: item => item.uiStatus
          })
        : [],
    [filter, items]
  )

  return {
    filter,
    setFilter,
    visibleFilters,
    groups,
    items: sortedItems,
    allItems: items,
    globalProgress,
    isEmpty: items.length === 0,
    isFilteredEmpty: sortedItems.length === 0 && items.length > 0,
    isLoading: !isDataLoaded
  }
}
